import { useEffect, useCallback, useState } from 'react';
// @mui
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import AvatarGroup, { avatarGroupClasses } from '@mui/material/AvatarGroup';
// routes
import { paths } from 'src/routes/paths';
import { useParams } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// utils
import { fShortenNumber } from 'src/utils/format-number';
// types
import { HTBaiViet } from 'src/types/htblog';
// components
import Iconify from 'src/components/iconify';
import Markdown from 'src/components/markdown';
import EmptyContent from 'src/components/empty-content';
// hooks
import useBlog from '../hooks/use-blog';
//
import BlogDetailsHero from '../blog-details-hero';
import BlogCommentList from '../blog-comment-list';
import BlogCommentForm from '../blog-comment-form';
import { PostDetailsSkeleton } from '../blog-skeleton';
import BlogDetailsToolbar from '../blog-details-toolbar';

// ----------------------------------------------------------------------

export default function BlogDetailsView() {
  const params = useParams();
  const { id } = params;

  const { blog, blogStatus, getBlog } = useBlog();
  const [comments, setComments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBlog = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);  
    try {
      await getBlog(Number(id)); 
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setIsLoading(false); 
    }
  }, [id, getBlog]);

  const fetchComments = useCallback(async () => {
    // Placeholder: implement comments API in hook when available
    setComments([]);
  }, []);

  useEffect(() => { 
    fetchBlog();
    fetchComments();
  }, [fetchBlog, fetchComments]);

  const renderSkeleton = <PostDetailsSkeleton />;

  const renderError = (
    <EmptyContent
      filled
      title="Không tìm thấy bài viết"
      action={
        <Button
          component={RouterLink}
          href={paths.dashboard.blog.root}
          startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
          sx={{ mt: 3 }}
        >
          Back to List
        </Button>
      }
      sx={{
        py: 20,
      }}
    />
  );

  const renderBlog = blog ? (
    <>
      <BlogDetailsToolbar
        backLink={paths.dashboard.blog.root}
        editLink={paths.dashboard.blog.edit(Number(blog.id))}
        liveLink={paths.blog.details(String(blog.id))}
        publish={blog.trangThai === 1 ? 'published' : 'draft'}
        onChangePublish={() => {}}
        publishOptions={[
          { value: 'published', label: 'Published' },
          { value: 'draft', label: 'Draft' }
        ]}
      />

      <BlogDetailsHero
        title={blog.tieuDe}
        author={{
          name: blog.htTaiKhoan?.tenTaiKhoan || '',
          avatarUrl: `/assets/images/avatar/avatar_${Math.floor(Math.random() * 24) + 1}.jpg`,
        }}
        createdAt={blog.ngayTao}
        coverUrl={blog.hinhAnh}
      />

      <Stack
        sx={{
          maxWidth: 720,
          mx: 'auto',
          mt: { xs: 5, md: 10 },
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 5 }}>
          {blog.moTaNgan}
        </Typography>

        <Markdown children={blog.noiDung} />

        <Stack
          spacing={3}
          sx={{
            py: 3,
            borderTop: (theme) => `dashed 1px ${theme.palette.divider}`,
            borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" flexWrap="wrap" spacing={1} />

          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  defaultChecked
                  size="small"
                  color="error"
                  icon={<Iconify icon="solar:heart-bold" />}
                  checkedIcon={<Iconify icon="solar:heart-bold" />}
                />
              }
              label={fShortenNumber(0)}
              sx={{ mr: 1 }}
            />

            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ typography: 'body2', color: 'text.disabled' }}>
              <Iconify icon="solar:eye-bold" width={16} />
              <span>{fShortenNumber(blog.luotXem)}</span>
            </Stack>
          </Stack>
        </Stack>

        <Stack direction="row" sx={{ mb: 3, mt: 5 }}>
          <Typography variant="h4">Comments</Typography>

          <Typography variant="subtitle2" sx={{ color: 'text.disabled' }}>
            (0)
          </Typography>
        </Stack>

        <BlogCommentForm />

        <Divider sx={{ mt: 5, mb: 2 }} />

        <BlogCommentList comments={comments} />
      </Stack>
    </>
  ) : null;

  let content;
  if (isLoading || blogStatus.loading) {
    content = renderSkeleton;
  } else if (!blog) {
    content = renderError;
  } else {
    content = renderBlog;
  }

  return (
    <Container maxWidth={false}>{content}</Container>
  );
}